import React, { useState } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getTenantFromToken } from 'utils/routeUtils';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { format, parseISO, isValid } from 'date-fns';
import ExportHistoryTable from './ExportHistoryTable';
import { generateExportLog } from 'api/endpoints/Export';
import { getSubCountyData } from 'api/endpoints/Location';
import { farmerApi } from 'api/endpoints/farmers';
import apiClient from 'api/client';

// Initial state for dates: today
const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const ALL_OPTION = { value: 'ALL', label: 'ALL' };

const defaultFilters = {
  farmer: ALL_OPTION,
  farm: ALL_OPTION,
  subCounty: ALL_OPTION,
  createdStartDate: getTodayDateString(),
  createdEndDate: getTodayDateString(),
  modifiedStartDate: getTodayDateString(),
  modifiedEndDate: getTodayDateString()
};

const ExportFarmlandReportScreen = () => {
  const navigate = useNavigate();
  const tenant = getTenantFromToken();
  const [filters, setFilters] = useState(defaultFilters);
  const [validationError, setValidationError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Used to manually refresh table

  // Key to force re-render of AsyncSelects when dependencies change
  const [asyncSelectKey, setAsyncSelectKey] = useState(Date.now());

  const handleDateChange = (field, date) => {
    const value = date && isValid(date) ? format(date, 'yyyy-MM-dd') : '';
    setFilters(prev => ({ ...prev, [field]: value }));
    setValidationError(''); // Clear validation on change
    setAsyncSelectKey(Date.now()); // Reload dropdowns with new dates
  };

  const validateDates = () => {
    if (
      filters.createdStartDate &&
      filters.createdEndDate &&
      new Date(filters.createdStartDate) > new Date(filters.createdEndDate)
    ) {
      setValidationError(
        'Created Start Date cannot be greater than Created End Date.'
      );
      return false;
    }
    if (
      filters.modifiedStartDate &&
      filters.modifiedEndDate &&
      new Date(filters.modifiedStartDate) > new Date(filters.modifiedEndDate)
    ) {
      setValidationError(
        'Modified Start Date cannot be greater than Modified End Date.'
      );
      return false;
    }
    return true;
  };

  const getDateFilters = () => {
    const apiFilters = {};
    if (filters.createdStartDate)
      apiFilters.created_start_date = filters.createdStartDate;
    if (filters.createdEndDate)
      apiFilters.created_end_date = filters.createdEndDate;
    if (filters.modifiedStartDate)
      apiFilters.modified_start_date = filters.modifiedStartDate;
    if (filters.modifiedEndDate)
      apiFilters.modified_end_date = filters.modifiedEndDate;
    return apiFilters;
  };

  const loadFarmers = async inputValue => {
    try {
      const response = await apiClient.post('digitization/v1/farmer/report', {
        cursor: 0,
        limit: 100,
        filters: {
          search_text: inputValue || '',
          is_active: true
        }
      });
      const data =
        response?.data?.results?.data ||
        response?.data?.data ||
        response?.data?.items ||
        [];
      const options = data.map(f => {
        const name = f['Farmer Name'] || f.farmer_name || f.name || 'Unknown';
        // Swap priority: Prefer readable codes (Alternative Farmer Code) over internal IDs (Farmer ID)
        const id =
          f['Alternative Farmer Code'] ||
          f.farmer_code ||
          f.code ||
          f['Farmer ID'] ||
          f.farmer_id ||
          f.id ||
          'N/A';
        const nin =
          f['ID Proof Number'] ||
          f['NIN Number of the Farmer'] ||
          f.id_proof_number ||
          f.nin ||
          f.nin_number ||
          'N/A';

        return {
          value: f.id || f.farmer_id || id,
          label: `${name} - ${id} - ${nin}`
        };
      });
      return [ALL_OPTION, ...options];
    } catch (e) {
      console.error('loadFarmers error:', e);
      return [ALL_OPTION];
    }
  };

  const loadFarms = async inputValue => {
    try {
      const apiFilters = {
        search_text: inputValue || '',
        is_active: true
      };
      if (filters.farmer && filters.farmer.value !== 'ALL') {
        apiFilters.farmer_id = filters.farmer.value;
      }
      const response = await apiClient.post('digitization/v1/farm/report', {
        cursor: 0,
        limit: 100,
        filters: apiFilters
      });
      const data =
        response?.data?.results?.data ||
        response?.data?.data ||
        response?.data?.items ||
        [];
      const options = data.map(f => {
        const farmName = f['Farm Name'] || f.farm_name || f.name || 'Unknown';
        // Prefer Farm ID V2 or code over the primary ID if it looks like a UUID
        const farmId =
          f['Farm ID V2'] ||
          f.farm_code ||
          f['Farm ID'] ||
          f.farm_id ||
          f.id ||
          'N/A';
        const nin =
          f['ID Proof Number'] ||
          f['NIN Number of the Farmer'] ||
          f.id_proof_number ||
          f.nin ||
          f.nin_number ||
          'N/A';

        return {
          value: f.id || f.farm_id || farmId,
          label: `${farmName} - ${farmId} - ${nin}`
        };
      });
      return [ALL_OPTION, ...options];
    } catch (e) {
      console.error('loadFarms error:', e);
      return [ALL_OPTION];
    }
  };

  const loadSubCounties = async inputValue => {
    try {
      // 1. Try master data API first with tenant filtering
      const response = await getSubCountyData({
        searchText: inputValue || '',
        tenantId: tenant
      });

      // Flexible data extraction to handle various response formats
      const results = response?.data?.results || response?.data || [];
      const data = Array.isArray(results) ? results : results.data || [];

      if (data.length > 0) {
        const options = data.map(sc => ({
          value: sc.id || sc.name || sc.value,
          label: sc.name || sc.label || sc.value
        }));
        return [ALL_OPTION, ...options];
      }

      // 2. Fallback: Aggregate from farmer reports if master data is empty
      const fallbackResponse = await apiClient.post(
        'digitization/v1/farmer/report',
        {
          cursor: 0,
          limit: 1000,
          filters: { is_active: true }
        }
      );

      const fallbackItems = fallbackResponse?.data?.results?.data || [];
      const subCounties = new Set();
      fallbackItems.forEach(item => {
        const sc = item['Sub County'] || item.sub_county || item.taluk;
        if (sc) subCounties.add(sc);
      });

      const fallbackOptions = Array.from(subCounties)
        .sort()
        .map(sc => ({
          value: sc,
          label: sc
        }));

      return [ALL_OPTION, ...fallbackOptions];
    } catch (e) {
      console.error('loadSubCounties error:', e);
      return [ALL_OPTION];
    }
  };

  const handleFarmerChange = val => {
    setFilters(prev => ({
      ...prev,
      farmer: val || ALL_OPTION,
      farm: ALL_OPTION, // Reset Farm
      subCounty: ALL_OPTION // Reset Sub County
    }));
    setAsyncSelectKey(Date.now()); // Trigger reload of dependent dropdowns
  };

  const handleFarmChange = val => {
    setFilters(prev => ({
      ...prev,
      farm: val || ALL_OPTION,
      subCounty: ALL_OPTION // Reset Sub County
    }));
    setAsyncSelectKey(Date.now());
  };

  const handleSubCountyChange = val => {
    setFilters(prev => ({
      ...prev,
      subCounty: val || ALL_OPTION
    }));
  };

  const handleSearchAndExport = async (format = 'excel') => {
    if (!validateDates()) return;

    setIsExporting(true);
    try {
      const apiFilters = { ...getDateFilters(), is_active: true };

      // Add IDs for filtering
      if (filters.farmer && filters.farmer.value !== 'ALL')
        apiFilters.farmer_id = filters.farmer.value;
      if (filters.farm && filters.farm.value !== 'ALL')
        apiFilters.farm_id = filters.farm.value;
      if (filters.subCounty && filters.subCounty.value !== 'ALL')
        apiFilters.sub_county = filters.subCounty.value;

      // Add Labels for display in History Table
      apiFilters.farmer_name_display = filters.farmer?.label || 'ALL';
      apiFilters.farm_name_display = filters.farm?.label || 'ALL';
      apiFilters.sub_county_display = filters.subCounty?.label || 'ALL';
      apiFilters.format_requested = format; // Store intent
      apiFilters.created_date_display =
        filters.createdStartDate && filters.createdEndDate
          ? `${format(
              parseISO(filters.createdStartDate),
              'dd-MM-yyyy'
            )} to ${format(parseISO(filters.createdEndDate), 'dd-MM-yyyy')}`
          : 'ALL';
      apiFilters.modified_date_display =
        filters.modifiedStartDate && filters.modifiedEndDate
          ? `${format(
              parseISO(filters.modifiedStartDate),
              'dd-MM-yyyy'
            )} to ${format(parseISO(filters.modifiedEndDate), 'dd-MM-yyyy')}`
          : 'ALL';

      await generateExportLog('farmland_report', apiFilters);
      toast.success(
        `Export as ${format.toUpperCase()} started in the background!`
      );
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to start export process.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearAll = () => {
    setFilters(defaultFilters);
    setValidationError('');
    setAsyncSelectKey(Date.now());
  };

  return (
    <Card className="mb-3">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center py-3">
        <h5 className="mb-0 text-primary fw-bold">
          <i className="fas fa-file-export me-2"></i> Export Farmland Report
        </h5>
      </Card.Header>
      <Card.Body>
        <div className="filter-section mb-4">
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semi-bold">Farmer Name</Form.Label>
                <AsyncSelect
                  key={`farmer-${asyncSelectKey}`}
                  cacheOptions
                  defaultOptions
                  loadOptions={loadFarmers}
                  value={filters.farmer}
                  onChange={handleFarmerChange}
                  placeholder="Search Farmer..."
                  isClearable
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semi-bold">Farm Name</Form.Label>
                <AsyncSelect
                  key={`farm-${asyncSelectKey}`}
                  cacheOptions
                  defaultOptions
                  loadOptions={loadFarms}
                  value={filters.farm}
                  onChange={handleFarmChange}
                  placeholder="Search Farm..."
                  isClearable
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semi-bold">Sub County</Form.Label>
                <AsyncSelect
                  key={`sc-${asyncSelectKey}`}
                  cacheOptions
                  defaultOptions
                  loadOptions={loadSubCounties}
                  value={filters.subCounty}
                  onChange={handleSubCountyChange}
                  placeholder="Search Sub County"
                  isClearable
                  classNamePrefix="react-select"
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group className="d-flex flex-column">
                <Form.Label className="fw-semi-bold">
                  Created On Start Date
                </Form.Label>
                <DatePicker
                  selected={
                    filters.createdStartDate
                      ? parseISO(filters.createdStartDate)
                      : null
                  }
                  onChange={date => handleDateChange('createdStartDate', date)}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="DD-MM-YYYY"
                  className="form-control shadow-none border-2"
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="d-flex flex-column">
                <Form.Label className="fw-semi-bold">
                  Created On End Date
                </Form.Label>
                <DatePicker
                  selected={
                    filters.createdEndDate
                      ? parseISO(filters.createdEndDate)
                      : null
                  }
                  onChange={date => handleDateChange('createdEndDate', date)}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="DD-MM-YYYY"
                  className="form-control shadow-none border-2"
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="d-flex flex-column">
                <Form.Label className="fw-semi-bold">
                  Modified On Start Date
                </Form.Label>
                <DatePicker
                  selected={
                    filters.modifiedStartDate
                      ? parseISO(filters.modifiedStartDate)
                      : null
                  }
                  onChange={date => handleDateChange('modifiedStartDate', date)}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="DD-MM-YYYY"
                  className="form-control shadow-none border-2"
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="d-flex flex-column">
                <Form.Label className="fw-semi-bold">
                  Modified On End Date
                </Form.Label>
                <DatePicker
                  selected={
                    filters.modifiedEndDate
                      ? parseISO(filters.modifiedEndDate)
                      : null
                  }
                  onChange={date => handleDateChange('modifiedEndDate', date)}
                  dateFormat="dd-MM-yyyy"
                  placeholderText="DD-MM-YYYY"
                  className="form-control shadow-none border-2"
                  autoComplete="off"
                />
              </Form.Group>
            </Col>
          </Row>

          {validationError && (
            <div className="text-danger mt-2 small fw-bold">
              <i className="fas fa-exclamation-circle me-1"></i>{' '}
              {validationError}
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4 pt-2">
            <Button
              variant="outline-secondary"
              onClick={handleClearAll}
              className="px-4 fw-bold"
            >
              Clear All
            </Button>
            <Button
              variant="success"
              onClick={() => handleSearchAndExport('excel')}
              disabled={isExporting}
              className="px-4 fw-bold border-0"
              style={{ backgroundColor: '#107c41' }} // Excel Green
            >
              <i className="fas fa-file-excel me-2"></i> Export as Excel
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSearchAndExport('csv')}
              disabled={isExporting}
              className="px-4 fw-bold border-0"
              style={{ backgroundColor: '#217346' }} // CSV/Greenish Blue
            >
              <i className="fas fa-file-csv me-2"></i> Export as CSV
            </Button>
          </div>
        </div>

        <hr />

        <h5 className="mb-3 mt-4">Export History</h5>
        <ExportHistoryTable
          refreshTrigger={refreshTrigger}
          setRefreshTrigger={setRefreshTrigger}
        />
      </Card.Body>
    </Card>
  );
};

export default ExportFarmlandReportScreen;
